import React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { needsWelcome } from '@/lib/onboarding';
import AppHeader from '@/components/AppHeader';
import BottomNav from './BottomNav';
import { Loader2 } from 'lucide-react';

// Every role's dashboard renders its own BrandHeader (leaf + wordmark
// + tagline + bell) as part of the refreshed home, so the global
// AppHeader would be duplicative there. Every other route — including
// role pages that aren't /dashboard — keeps the shared header.
function shouldHideAppHeader(user, pathname) {
  return pathname === '/dashboard'
    && ['farmer', 'manufacturer', 'distributor', 'shopkeeper', 'offtaker']
      .includes(user?.user_type);
}

export default function DashboardLayout() {
  const { isAuthenticated, loading, user } = useAuth();
  const { pathname } = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-farm-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // The first-time wizard comes first (its steps are all required).
  if (needsWelcome(user)) {
    return <Navigate to="/welcome" replace />;
  }

  const hideAppHeader = shouldHideAppHeader(user, pathname);
  // The homes sit on light green; other screens keep the neutral
  // background from --background.
  const groundClass = hideAppHeader ? 'bg-farm-mint' : 'bg-background';

  return (
    <div className={`min-h-screen ${groundClass} pb-20 safe-area-pt`}>
      {!hideAppHeader && <AppHeader />}
      {/* Container lifts with the viewport instead of staying 512 px
          wide on every screen. Mobile stays as before; tablets get a
          roomier column; desktops stop looking like a phone preview
          centred on a huge empty background. The matching width on
          BottomNav keeps the five tabs sitting under the content. */}
      <main className="max-w-lg md:max-w-3xl lg:max-w-5xl mx-auto">
        <Outlet />
      </main>
      <BottomNav />
    </div>
  );
}
